import { useLayoutEffect, useRef, useState, type DragEvent } from "react";

type EditorReorderMove = Readonly<{ from: number; to: number }>;
type EditorReorderOptions = Readonly<{
  count: number;
  disabled: boolean;
  onReorder(move: EditorReorderMove): void;
}>;
type Direction = -1 | 1;
type FocusTarget = Readonly<{ index: number; direction: Direction }>;

export function useEditorReorder({
  count,
  disabled,
  onReorder,
}: EditorReorderOptions) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const buttons = useRef<Record<Direction, (HTMLButtonElement | null)[]>>({
    [-1]: [],
    1: [],
  });
  const pendingFocus = useRef<FocusTarget | null>(null);

  useLayoutEffect(() => {
    const target = pendingFocus.current;

    if (target === null) return;

    pendingFocus.current = null;
    buttons.current[target.direction][target.index]?.focus();
  });

  function buttonProps({ index, direction }: FocusTarget) {
    const to = index + direction;
    return {
      ref: (button: HTMLButtonElement | null) => {
        buttons.current[direction][index] = button;
      },
      disabled:
        disabled || (direction === -1 ? index === 0 : index === count - 1),
      focusableWhenDisabled: !disabled,
      onClick: () => {
        if (disabled || to < 0 || to >= count) return;

        pendingFocus.current = { index: to, direction };
        onReorder({ from: index, to });
      },
    };
  }

  function dragProps(index: number) {
    return {
      draggable: !disabled,
      onDragStart: () => setDragIndex(index),
      onDragEnd: () => setDragIndex(null),
      onDragOver: (event: DragEvent<HTMLElement>) => event.preventDefault(),
      onDrop: () => {
        if (!disabled && dragIndex !== null && dragIndex !== index)
          onReorder({ from: dragIndex, to: index });

        setDragIndex(null);
      },
    };
  }

  return { buttonProps, dragProps };
}
